import * as THREE from 'three';

export interface WorldMarker {
  /** Camera-relative position, metres, float32 precision. */
  position: THREE.Vector3;
  color: number;
}

/**
 * A handful of colour-coded points in world space — orbit apsides, an impact
 * site, the selected navigation target.
 *
 * Fixed pixel size (`sizeAttenuation: false`) rather than a real-world size:
 * these mark a location, not an object, so they should stay readable whether
 * the thing they're marking is a hundred metres away or the far side of a
 * planet's orbit.
 */
export class WorldMarkerLayer {
  readonly points: THREE.Points;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsMaterial;
  private capacity = 0;

  constructor(pixelSize = 8) {
    this.geometry = new THREE.BufferGeometry();
    this.material = new THREE.PointsMaterial({
      size: pixelSize,
      sizeAttenuation: false,
      vertexColors: true,
      transparent: true,
      depthTest: true,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
  }

  set(markers: WorldMarker[]): void {
    if (markers.length === 0) {
      this.points.visible = false;
      return;
    }
    this.points.visible = true;

    if (this.capacity !== markers.length) {
      this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(markers.length * 3), 3));
      this.geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(markers.length * 3), 3));
      this.capacity = markers.length;
    }
    const pos = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const col = this.geometry.getAttribute('color') as THREE.BufferAttribute;
    const c = new THREE.Color();
    for (let i = 0; i < markers.length; i++) {
      const m = markers[i];
      pos.setXYZ(i, m.position.x, m.position.y, m.position.z);
      c.setHex(m.color);
      col.setXYZ(i, c.r, c.g, c.b);
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;
    this.geometry.setDrawRange(0, markers.length);
    this.geometry.computeBoundingSphere();
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
